
import React, { useState } from 'react';
import AppTable from 'components/basic/AppTable';
import AppLoader from 'components/basic/AppLoader';
import { useItems } from 'hooks/atoms/items-hook';
import { LoaderSkeleton } from 'components/basic/LoaderSkeleton';
// import usePermissions from 'hooks/permissions-hooks';
// import actions from 'roles-actions-permissions/actions';
// import { LabFilesCtx } from 'pages/LabFilesPage';
import { Button, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle } from '@material-ui/core';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { parseDate } from 'components/business/consents/ConsentsTable';
import { makeStyles } from '@material-ui/core/styles';
import axiosClient from 'http/axios-client';
import { useAlerts } from 'hooks/alerts-hooks';
import { filterTypes } from '../../basic/AdvancedFilters';
import LabFilesDialog from './LabFilesDialog';

const useStyles = makeStyles(() => {
  return {
    dialogPaper: {
      width: '100%',
      maxWidth: 1200,
    },
    dialogTitle: {
      padding: '16px 24px',
      borderBottom: '1px solid #e0e0e0',
    },
    dialogContent: {
      padding: 0,
    },
    table: {
      minWidth: 650,
    },
    header: {
      backgroundColor: '#f5f5f5',
    },
    headerCell: {
      fontWeight: 'bold',
    },
    row: {
      '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
      },
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
    },
    cell: {
      padding: '12px 16px',
    },
    button: {
      backgroundColor: '#d0d2d3ff',
    },
    greenButton: {
      color: '#ffffff',
      backgroundColor: 'rgba(7, 177, 44, 1)',
      '&:hover': {
        backgroundColor: '#033a11ff',
      },
    },
  };
});

async function importToDataBase(data, selected, alertError, alertSuccess, setImporting, refetch) {
  setImporting(true);
  const dataToImport = data.map((item) => {
    return { serial: item.sample_name,
      metadata: {
        nucleic_acid: item?.nucleic_acid,
        a260: item?.a260,
        a280: item?.a280,
        a260_a230: item?.a260_a230,
        a260_a280: item?.a260_a280,
        baseline_absorbance: item?.baseline_absorbance,
        baseline_correction: item?.baseline_correction,
        nucleic_acid_factor: item?.nucleic_acid_factor,
      } };
  });
  // add metadata to serial number
  try {
    await axiosClient.put('/serial-numbers/bulk-metadata-update', dataToImport);
    alertSuccess('Imported completed');
  } catch (error) {
    alertError(`${ error }`);
  }

  // 3. Update csv file to: 'Imported'
  try {
    await axiosClient.put(`/runs/${ selected.id }/`, { status: 'Imported' });
  } catch (error) {
    alertError(`${ error }`);
    return null;
  }

  setImporting(false);
  alertSuccess('Imported completed');
  refetch();
  return null;
}

function LabFilesTable() {
  const classes = useStyles();
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ selected, setSelected ] = useState({});
  const [ openDialog, setOpenDialog ] = useState(false);
  const { alertSuccess, alertError } = useAlerts();
  const [ importing, setImporting ] = useState(false);
  const [ toImport, setToImport ] = useState([]);

  const {
    items: files,
    itemsLoading: filesLoading,
    refetch,
    itemsError: filesError,
    pagination,
  } = useItems(CollectionKeys.LAB_FILES, { filterQuery: {
    ...filterQuery,
  } });

  if (filesLoading) {
    return <AppLoader />;
  }

  if (filesError) {
    return (
      <span>
        Error:
        {filesError}
      </span>
    );
  }

  const tableColumns = [
    { id: 1, label: 'File Name', field: 'file_name' },
    { id: 2,
      label: 'Modified Date',
      field: 'updated_at',
      customRenderer({ row }) {
        return (
          <>
            {parseDate(row?.updated_at)}
          </>
        );
      } },
    { id: 3,
      label: 'Status',
      field: 'status',
      customRenderer({ row }) {
        return (
          <>
            {row?.status === 'Imported' ? 'Imported' : 'Not Imported'}
          </>
        );
      } },
    { id: 4,
      label: 'Action',
      customRenderer({ row }) {
        return (
          <>
            <Button
              variant='contained'
              color='secondary'
              onClick={ () => {
                setSelected(row);
                setOpenDialog(true);
              } }
            >
              View and Import
            </Button>
          </>
        );
      } },
  ];

  if (importing) {
    return <LoaderSkeleton />;
  }

  return (
    <div style={ { marginTop: '20px' } }>
      <AppTable
        columns={ tableColumns }
        refetch={ refetch }
        paginationProps={ pagination }
        data={ files }
        advancedFiltersProps={ {
          properties: [
            { value: 'status',
              label: 'Status',
              type: filterTypes.CATEGORY,
              options: [
                { value: 'Imported', label: 'Imported' },
                { value: 'Not_Imported', label: 'Not Imported' },
              ] }],
          initialAndFilterOptions: [],
          onQueryChange: setFilterQuery,
        } }

      />
      <Dialog
        open={ openDialog }
        onClose={ () => { setSelected({}); setOpenDialog(false); } }
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        classes={ { paper: classes.dialogPaper } }
      >
        <DialogTitle
          className={ classes.dialogTitle }
          id='alert-dialog-title'
        >
          Import DNA Concentrations
        </DialogTitle>
        <DialogContent className={ classes.dialogContent }>
          <DialogContentText id='alert-dialog-description' className={ classes.tableContainer }>
            <LabFilesDialog
              selectedFile={ selected }
              toImport={ toImport }
              setToImport={ setToImport }
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={ selected?.status !== 'Not_Imported' }
            className={ classes.greenButton }
            onClick={ async () => {
              setOpenDialog(false);
              await importToDataBase(toImport, selected, alertError, alertSuccess, setImporting, refetch);
              setToImport([]);
            } }
          >
            Import (
            {toImport.length}
            )
          </Button>
          <Button
            className={ classes.button }
            onClick={ () => { setToImport([]); setOpenDialog(false); } }
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default LabFilesTable;
